import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { db, storage, ensureAuthenticated } from "../firebase";
import { addDoc, collection, getDocs, limit, query, serverTimestamp, where, doc, setDoc } from "firebase/firestore";
import { ref, uploadBytes } from "firebase/storage";
import { X } from 'lucide-react';
import Button from "../components/Button";
import OpenGalleryCard from "../components/OpenGalleryCard";
import MemoryInput from "../components/MemoryInput";
import SegmentedControl from "../components/SegmentedControl";
import { useCurrentMemorial } from "../context/CurrentMemorialContext";
export default function MemorialUpload() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [memorial, setMemorial] = useState(null);

  const [activeTab, setActiveTab] = useState("note");
  const [noteText, setNoteText] = useState("");
  const [imageDescription, setImageDescription] = useState("");
  const [author, setAuthor] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const fileInputRef = useRef(null);
  const { setCurrent } = useCurrentMemorial();

  useEffect(() => {
    const load = async () => {
      const q = query(collection(db, "memorials"), where("slug", "==", slug), limit(1));
      const snap = await getDocs(q);
      if (!snap.empty) {
        const d = snap.docs[0];
        setMemorial({ id: d.id, ...d.data() });
        setCurrent({ slug, memorialId: d.id });
      }
    };
    load();
  }, [slug]);

  const handleAddNote = async () => {
    if (!noteText.trim() || !memorial?.id || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const user = await ensureAuthenticated();
      await addDoc(collection(db, "memorials", memorial.id, "submissions"), {
        type: "note",
        text: noteText.trim(),
        authorName: author.trim() || "Anonymous",
        submitterUid: user.uid,
        createdAt: serverTimestamp(),
        status: "pending",
        tempImagePath: null,
      });
      navigate(`/m/${slug}`);
    } catch (e) {
      console.error("Upload note error:", e);
      let msg = e.message || "Unknown error";
      if (msg.includes("Missing or insufficient permissions")) msg = "Missing permissions. Please try signing in again.";
      alert(`Error: ${msg}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleImageSelect = (e) => {
    if (e.target.files?.[0]) {
      const file = e.target.files[0];
      setSelectedImageFile(file);
      setSelectedImage(URL.createObjectURL(file));
    }
  };

  const handleImageUpload = async () => {
    if (!selectedImageFile || !memorial?.id || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const user = await ensureAuthenticated();

      // 1. Generate ID client-side
      const submissionsRef = collection(db, "memorials", memorial.id, "submissions");
      const newSubmissionRef = doc(submissionsRef);
      const submissionId = newSubmissionRef.id;
      
      // 2. Upload to Storage
      const path = `memorials/${memorial.id}/pending/${submissionId}/${selectedImageFile.name.replace(/\s/g, "_")}`;
      const storageRef = ref(storage, path);
      try {
        await uploadBytes(storageRef, selectedImageFile);
      } catch (storageErr) {
        throw new Error(`Storage upload failed: ${storageErr.message} (Code: ${storageErr.code})`);
      }
      
      // 3. Create Firestore document with all data at once
      try {
        await setDoc(newSubmissionRef, {
          type: "image",
          text: imageDescription.trim() || null,
          authorName: author.trim() || "Anonymous",
          submitterUid: user.uid,
          createdAt: serverTimestamp(),
          status: "pending",
          tempImagePath: path,
        });
      } catch (firestoreErr) {
        throw new Error(`Firestore create failed: ${firestoreErr.message} (Code: ${firestoreErr.code})`);
      }

      navigate(`/m/${slug}`);
    } catch (e) {
      console.error("Upload process error:", e);
      let msg = e.message || "Unknown error";
      if (msg.includes("Storage upload")) msg = `Image upload failed (Storage): ${e.message}`;
      else if (msg.includes("Firestore create")) msg = `Could not save your upload (Firestore): ${e.message}`;
      else if (msg.includes("Missing or insufficient permissions")) msg = "Missing permissions. Please try signing in again.";

      alert(`Error: ${msg}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full min-h-screen bg-[var(--color-bg)] text-[#44231E] overflow-x-hidden">
      <div className="pt-10 px-6 pb-48 min-h-screen overflow-y-auto">
        <div className="w-full">
          {activeTab === "note" ? (
            <>
              <MemoryInput
                value={noteText}
                onChange={setNoteText}
                author={author}
                onAuthorChange={setAuthor}
                minHeight={428}
                justifyBetween
              />
              <div className="flex justify-end mt-6">
                <Button trailingIconName="arrowRightUp" onClick={handleAddNote} disabled={isSubmitting || !noteText.trim()}>
                  Upload memory note
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="w-full h-[min(192px,_30vh)] mb-6">
                {selectedImage ? (
                  <div className="w-full h-full relative">
                    <img src={selectedImage} alt="Selected" className="w-full h-full object-cover rounded-[15px]" />
                    <button
                      onClick={() => { setSelectedImage(null); setSelectedImageFile(null); }}
                      className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/80 flex items-center justify-center"
                    >
                      <X size={14} color="#44231E" strokeWidth={2} />
                    </button>
                  </div>
                ) : (
                  <OpenGalleryCard onClick={() => fileInputRef.current?.click()} />
                )}
              </div>

              <div className="mb-6">
                <MemoryInput
                  value={imageDescription}
                  onChange={setImageDescription}
                  author={author}
                  onAuthorChange={setAuthor}
                  placeholder="Write picture description..."
                  authorPlaceholder="Write your name..."
                  justifyBetween
                  className="h-[min(192px,_30vh)]"
                />
              </div>

              <div className="flex justify-end">
                <Button trailingIconName="arrowRightUp" onClick={handleImageUpload} disabled={isSubmitting || !selectedImageFile}>
                  Upload photo
                </Button>
              </div>
            </>
          )}

          {/* Guest upload consent notice */}
          <p style={{ marginTop: 20, fontSize: 12, fontFamily: 'Poppins', color: '#B9A68E', lineHeight: 1.5 }}>
            By uploading, you confirm you have the right to share this content and you accept our{' '}
            <Link to="/legal/terms" target="_blank" style={{ color: '#8E7C69', textDecoration: 'underline' }}>Terms of Use</Link>
            {' '}and{' '}
            <Link to="/legal/privacy" target="_blank" style={{ color: '#8E7C69', textDecoration: 'underline' }}>Privacy Policy</Link>.
          </p>
        </div>
      </div>

      {/* Takes the bottom-left slot the pill nav normally occupies
          (nav is hidden on this page) and mirrors its shape/spacing.
          The close FAB sits at right:24, so the two pills flank the
          bottom of the screen symmetrically. */}
      <div className="fixed z-50" style={{ bottom: 24, left: 24 }}>
        <SegmentedControl value={activeTab} onChange={setActiveTab} />
      </div>

      <input type="file" ref={fileInputRef} onChange={handleImageSelect} accept="image/*" className="hidden" />
    </div>
  );
}
